import React, { useEffect, useRef, useState } from 'react';
import { searchPlayers, createPlayer } from '../services/api.js';

// ============================================================
// Types
// ============================================================

export interface PlayerOption {
  id: string;
  name: string;
  email?: string;
  /** True when this player was resolved from the "recently played" list */
  isRecent?: boolean;
}

interface PlayerSelectorProps {
  value: string;
  onChange: (name: string) => void;
  placeholder?: string;
  /** List of recent opponents — fetched by parent once and passed in */
  recentPlayers: PlayerOption[];
  /** Input/button label for accessibility */
  'aria-label'?: string;
  inputStyle?: React.CSSProperties;
  /** Brand colors for theming */
  primaryColor: string;
  textColor: string;
  /** Player names already assigned to other slots — excluded from dropdown */
  excludeNames?: string[];
}

// ============================================================
// Sub-component: New Player inline form
// ============================================================

interface NewPlayerFormProps {
  initialName: string;
  onConfirm: (name: string, email?: string) => void;
  onCancel: () => void;
  primaryColor: string;
  textColor: string;
}

function NewPlayerForm({ initialName, onConfirm, onCancel, primaryColor, textColor }: NewPlayerFormProps) {
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onConfirm(trimmed, email.trim() || undefined);
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '13px 14px',
    fontSize: '1rem',
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.1)',
    color: textColor,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, opacity: 0.7, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        New Player
      </div>
      <input
        ref={nameRef}
        style={fieldStyle}
        type="text"
        placeholder="Player name (required)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
        aria-label="New player name"
      />
      <input
        style={fieldStyle}
        type="email"
        placeholder="Email (optional — for future invites)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
        aria-label="New player email (optional)"
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleConfirm}
          disabled={!name.trim()}
          style={{
            flex: 1,
            padding: '13px',
            fontSize: '0.95rem',
            fontWeight: 700,
            border: 'none',
            borderRadius: '8px',
            background: name.trim() ? primaryColor : 'rgba(255,255,255,0.1)',
            color: '#fff',
            cursor: name.trim() ? 'pointer' : 'not-allowed',
            touchAction: 'manipulation',
          }}
        >
          Add Player
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '13px 18px',
            fontSize: '0.95rem',
            fontWeight: 600,
            border: '2px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            background: 'transparent',
            color: textColor,
            cursor: 'pointer',
            touchAction: 'manipulation',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Main component
// ============================================================

const DEBOUNCE_MS = 250;

/**
 * PlayerSelector — smart dropdown for authenticated users.
 * Shows recently played opponents + typeahead search + "New Player..." option.
 * Designed for 10" tablet: 48px+ tap targets, large text.
 */
export function PlayerSelector({
  value,
  onChange,
  placeholder = 'Search or select player…',
  recentPlayers,
  primaryColor,
  textColor,
  inputStyle,
  excludeNames = [],
  'aria-label': ariaLabel,
}: PlayerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlayerOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showNewPlayerForm, setShowNewPlayerForm] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Close on outside click ───────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // ── Debounced search ─────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { players } = await searchPlayers(query);
        setSearchResults(players);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, isOpen]);

  // ── Dropdown open/close ──────────────────────────────────

  const open = () => {
    setIsOpen(true);
    setQuery('');
    setSearchResults([]);
    setShowNewPlayerForm(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const close = () => {
    setIsOpen(false);
    setQuery('');
    setShowNewPlayerForm(false);
  };

  // ── Selection ────────────────────────────────────────────

  const select = (player: PlayerOption) => {
    onChange(player.name);
    close();
  };

  const handleNewPlayerConfirm = async (name: string, email?: string) => {
    try {
      // Best-effort create — if it fails, we still have the name
      await createPlayer(name, email);
    } catch {
      // Non-blocking — the player name is still usable
    }
    onChange(name);
    close();
  };

  // ── Build options list ───────────────────────────────────

  // Normalise exclude list once for efficient lookups
  const excludeSet = new Set(excludeNames.map((n) => n.trim().toLowerCase()));

  const filterExcluded = (players: PlayerOption[]) =>
    excludeSet.size === 0
      ? players
      : players.filter((p) => !excludeSet.has(p.name.trim().toLowerCase()));

  // When query is empty, show recent players; otherwise show search results
  const displayPlayers: PlayerOption[] =
    query.length > 0
      ? filterExcluded(searchResults)
      : filterExcluded(recentPlayers.map((p) => ({ ...p, isRecent: true })));

  // ── Styles ───────────────────────────────────────────────

  const triggerStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1.1rem',
    border: `2px solid ${isOpen ? primaryColor : 'rgba(255,255,255,0.15)'}`,
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.08)',
    color: value ? textColor : 'rgba(255,255,255,0.4)',
    outline: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    touchAction: 'manipulation',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    ...inputStyle,
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 100,
    backgroundColor: '#1e2130',
    border: `2px solid ${primaryColor}`,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.05)',
    color: textColor,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const sectionLabelStyle: React.CSSProperties = {
    padding: '8px 16px 4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    opacity: 0.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: textColor,
  };

  const itemStyle = (highlighted: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    minHeight: '52px',
    fontSize: '1rem',
    color: highlighted ? '#ffffff' : textColor,
    background: highlighted ? `${primaryColor}33` : 'transparent',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    gap: '10px',
    touchAction: 'manipulation',
    transition: 'background 0.1s',
  });

  const newPlayerItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 16px',
    minHeight: '52px',
    fontSize: '1rem',
    fontWeight: 600,
    color: primaryColor,
    cursor: 'pointer',
    gap: '10px',
    touchAction: 'manipulation',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger button — shows current value or placeholder */}
      <button
        type="button"
        onClick={isOpen ? close : open}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        style={triggerStyle as React.CSSProperties}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        <ChevronIcon open={isOpen} color={textColor} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={dropdownStyle} role="listbox" aria-label={ariaLabel}>
          {showNewPlayerForm ? (
            <NewPlayerForm
              initialName={query}
              onConfirm={handleNewPlayerConfirm}
              onCancel={() => setShowNewPlayerForm(false)}
              primaryColor={primaryColor}
              textColor={textColor}
            />
          ) : (
            <>
              {/* Search input */}
              <input
                ref={inputRef}
                style={searchInputStyle}
                type="text"
                placeholder="Type to search players…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search players"
              />

              {/* Options list */}
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {/* Section header */}
                {displayPlayers.length > 0 && (
                  <div style={sectionLabelStyle}>
                    {query.length > 0 ? 'Search results' : 'Recently played'}
                  </div>
                )}

                {/* Loading */}
                {isSearching && (
                  <div style={{ padding: '14px 16px', opacity: 0.5, fontSize: '0.9rem', color: textColor }}>
                    Searching…
                  </div>
                )}

                {/* Player items */}
                {!isSearching &&
                  displayPlayers.map((player) => (
                    <div
                      key={player.id}
                      role="option"
                      aria-selected={player.name === value}
                      style={itemStyle(player.name === value)}
                      onClick={() => select(player)}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLDivElement).style.background = `${primaryColor}22`;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLDivElement).style.background =
                          player.name === value ? `${primaryColor}33` : 'transparent';
                      }}
                    >
                      <PlayerAvatar name={player.name} size={32} primaryColor={primaryColor} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {player.name}
                        </div>
                        {player.email && (
                          <div style={{ fontSize: '0.78rem', opacity: 0.55, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {player.email}
                          </div>
                        )}
                      </div>
                      {player.name === value && (
                        <CheckIcon color={primaryColor} />
                      )}
                    </div>
                  ))}

                {/* No results message */}
                {!isSearching && query.length > 0 && displayPlayers.length === 0 && (
                  <div style={{ padding: '14px 16px', opacity: 0.5, fontSize: '0.9rem', color: textColor }}>
                    No players found for "{query}"
                  </div>
                )}

                {/* New Player option */}
                <div
                  role="option"
                  style={newPlayerItemStyle}
                  onClick={() => setShowNewPlayerForm(true)}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}
                >
                  <PlusIcon color={primaryColor} />
                  <span>New Player…</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Micro-components (inline SVG icons, avatar)
// ============================================================

function ChevronIcon({ open, color }: { open: boolean; color: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}
      aria-hidden="true"
    >
      <path d="M3 6l5 5 5-5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon({ color }: { color: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 9l4 4 8-8" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="10" cy="10" r="9" stroke={color} strokeWidth="1.5" />
      <path d="M10 6v8M6 10h8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PlayerAvatar({ name, size, primaryColor }: { name: string; size: number; primaryColor: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `${primaryColor}44`,
        border: `1.5px solid ${primaryColor}66`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.38,
        fontWeight: 700,
        color: primaryColor,
        flexShrink: 0,
        letterSpacing: '-0.02em',
      }}
    >
      {initials || '?'}
    </div>
  );
}
